import { cpfValido } from '@acme/common'
import { zod } from '@acme/core'
import { funcionarioCentroCustoSchema } from 'service/api'
import { z } from 'zod'

const snSchema = z
    .boolean()
    .nullish()
    .transform(value => (value === true ? 'S' : value === false ? 'N' : null))

/**
 * Um centro escolhido sem data inicial viraria vínculo sem começo — a API
 * grava `dtInicial` como está e o funcionário sai do escopo de quem filtra por
 * centro de custo. Vale para o objeto do formulário e para a lista que a
 * edição recebe da API.
 */
const centroCustoDoFuncionarioSchema = funcionarioCentroCustoSchema
    .deepPartial()
    .array()
    .nullish()
    .or(funcionarioCentroCustoSchema.deepPartial().nullish())
    .refine(
        data => {
            if (Array.isArray(data))
                return data.every(item =>
                    item.CentroCusto ? !!item.dtInicial : true,
                )
            else if (data?.CentroCusto) return !!data.dtInicial
            return true
        },
        {
            message: 'Campo obrigatório',
            path: ['dtInicial'],
        },
    )

export const funcionarioSchema = z
    .object({
        id: z.number().optional(),
        matricula: z.coerce
            .number()
            .max(9999999999)
            .nullish()
            .transform(value => value || undefined),
        empresaId: z.coerce.number().min(1, 'Campo obrigatório'),
        nome: z.string().min(1, 'Campo obrigatório'),
        pis: z
            .string()
            .min(11, 'Campo obrigatório')
            .transform(value => value.replace(/\D/g, '')),
        regraCalculo: z.coerce.number().min(1, 'Campo obrigatório'),
        tabelaHorarioId: z.coerce.number().optional(),
        horarioNoturno: snSchema.default(false),
        entradaEntre231: snSchema.default(false),
        repSupervisor: snSchema.default(false),
        registroApp: snSchema.default(false),
        registroWeb: snSchema.default(false),
        ajustesPonto: snSchema.default(false),
        verSaldoHoras: snSchema.default(false),
        recebeValeAlimentacao: snSchema.default(true),
        expFolhaHorasExtras: snSchema.default(true),
        expFolhaFaltas: snSchema.default(true),
        expFolhaAdicionalNoturno: snSchema.default(true),
        expFolhaHorasNormais: snSchema.default(true),
        expFolhaHorasTrabalhar: snSchema.default(true),
        desconsiderarFeriados: snSchema.default(false),
        cpf: z
            .string()
            .min(11, 'Campo obrigatório')
            .refine(value => cpfValido(value), { message: 'CPF inválido' })
            .transform(value => value.replace(/\D/g, '')),
        uf: z.string().min(2, 'Campo obrigatório'),
        bonusValeAlimentacao: z.coerce.number().min(0).nullish(),
        dtNascimento: z.coerce
            .date({
                invalid_type_error: 'Formato de data inválido',
            })
            .nullish(),
        funcao: z.string().default(''),
        dtAdmissao: z.coerce.date({ invalid_type_error: 'Campo obrigatório' }),
        dtRescisao: z
            .string()
            .or(z.date({ invalid_type_error: 'Campo obrigatório' }))
            .nullish()
            .optional()
            .transform(value => value || null),
        Cidade: zod.CidadeSchema.omit({
            id: true,
            createdAt: true,
            updatedAt: true,
        })
            .nullable()
            .refine(value => value !== null, {
                message: 'Cidade é obrigatória',
            }),
        FuncionarioLocais: zod.FuncionarioLocaisSchema.omit({
            createdAt: true,
        })
            .deepPartial()
            .array()
            .nullish(),
        FuncionarioCentroCusto: centroCustoDoFuncionarioSchema,
    })
    .passthrough()

/**
 * Tira `FuncionarioCentroCusto` do payload quando o formulário só tem as datas
 * soltas — sem centro escolhido não há vínculo a criar, e o campo presente faz
 * a API tentar associar um centro inexistente.
 */
export function semCentroCustoVazio<T extends Record<string, any>>(data: T): T {
    const centroCusto = data.FuncionarioCentroCusto

    if (Array.isArray(centroCusto) || centroCusto?.CentroCusto) return data

    const { FuncionarioCentroCusto: _semCentro, ...resto } = data

    return resto as unknown as T
}
